import {
  type Address,
  createPublicClient,
  createWalletClient,
  custom,
  getAddress,
  type Hex,
  numberToHex,
  type EIP1193Provider as ViemEIP1193Provider,
} from 'viem'
import { mainnet, sepolia } from 'viem/chains'

export type EIP1193Provider = ViemEIP1193Provider & {
  isMetaMask?: boolean
  providers?: EIP1193Provider[]
  on?: (event: string, listener: (...args: unknown[]) => void) => void
  removeListener?: (event: string, listener: (...args: unknown[]) => void) => void
}

type EIP6963ProviderDetail = {
  info: { rdns: string; name: string }
  provider: EIP1193Provider
}

declare global {
  interface Window {
    ethereum?: EIP1193Provider
  }
}

const isOfficialMetaMaskProvider = (detail: EIP6963ProviderDetail) =>
  detail.info.rdns === 'io.metamask' || detail.info.rdns.startsWith('io.metamask.')

const getLegacyMetaMaskProvider = () => {
  const injectedProvider = window.ethereum
  if (!injectedProvider) return null

  const metaMaskProvider = injectedProvider.providers?.find((provider) => provider.isMetaMask)
  return metaMaskProvider ?? (injectedProvider.isMetaMask ? injectedProvider : null)
}

export const discoverMetaMask = async (timeoutMs = 250): Promise<EIP1193Provider | null> => {
  const discovered: EIP6963ProviderDetail[] = []

  const onProvider = (event: Event) => {
    const detail = (event as CustomEvent<EIP6963ProviderDetail>).detail
    if (detail) discovered.push(detail)
  }

  window.addEventListener('eip6963:announceProvider', onProvider)
  window.dispatchEvent(new Event('eip6963:requestProvider'))
  await new Promise((resolve) => window.setTimeout(resolve, timeoutMs))
  window.removeEventListener('eip6963:announceProvider', onProvider)

  return (
    discovered.find(isOfficialMetaMaskProvider)?.provider ??
    getLegacyMetaMaskProvider() ??
    discovered.find(({ provider }) => provider.isMetaMask)?.provider ??
    null
  )
}

const normalizeAccounts = (value: unknown): Address[] => {
  if (!Array.isArray(value)) return []
  return value.flatMap((account) => {
    try {
      return typeof account === 'string' ? [getAddress(account)] : []
    } catch {
      return []
    }
  })
}

export const getConnectedAccounts = async (provider: EIP1193Provider): Promise<Address[]> =>
  normalizeAccounts(await provider.request({ method: 'eth_accounts' }))

export const connectMetaMask = async (provider: EIP1193Provider): Promise<Address> => {
  const accounts = normalizeAccounts(await provider.request({ method: 'eth_requestAccounts' }))
  if (!accounts[0]) throw new Error('MetaMask did not return an account')
  return accounts[0]
}

export const disconnectMetaMask = async (provider: EIP1193Provider): Promise<void> => {
  await provider.request({
    method: 'wallet_revokePermissions',
    params: [{ eth_accounts: {} }],
  })
}

export const getProviderChainId = async (provider: EIP1193Provider): Promise<number> => {
  const value = await provider.request({ method: 'eth_chainId' })
  if (typeof value !== 'string' || !/^0x[0-9a-f]+$/i.test(value)) {
    throw new Error('MetaMask returned an invalid chain ID')
  }
  const chainId = Number(BigInt(value))
  if (!Number.isSafeInteger(chainId) || chainId <= 0) throw new Error('Invalid wallet chain ID')
  return chainId
}

const getDepositChain = (chainId: number) => {
  if (chainId === mainnet.id) return mainnet
  if (chainId === sepolia.id) return sepolia
  throw new Error(`Unsupported deposit chain ID: ${chainId}`)
}

export const switchProviderChain = async (
  provider: EIP1193Provider,
  chainId: number,
  rpcUrl?: string,
) => {
  const chain = getDepositChain(chainId)
  const chainIdHex = numberToHex(chainId)
  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: chainIdHex }],
    })
  } catch (error) {
    const code =
      typeof error === 'object' && error && 'code' in error ? Number(error.code) : undefined
    // Add the network only when it is unknown to the wallet; preserve rejections and other errors.
    if (code !== 4902 || !rpcUrl) throw error
    await provider.request({
      method: 'wallet_addEthereumChain',
      params: [
        {
          chainId: chainIdHex,
          chainName: chain.name,
          nativeCurrency: chain.nativeCurrency,
          rpcUrls: [rpcUrl],
          blockExplorerUrls: [chain.blockExplorers.default.url],
        },
      ],
    })
  }
}

export const createMetaMaskClients = (provider: EIP1193Provider, chainId: number) => {
  const chain = getDepositChain(chainId)
  return {
    publicClient: createPublicClient({ chain, transport: custom(provider) }),
    walletClient: createWalletClient({ chain, transport: custom(provider) }),
  }
}

export const signPermitTypedData = async (
  provider: EIP1193Provider,
  account: Address,
  typedData: unknown,
): Promise<Hex> => {
  const signature = await provider.request({
    method: 'eth_signTypedData_v4',
    params: [account, JSON.stringify(typedData)],
  })
  if (typeof signature !== 'string' || !/^0x[0-9a-f]+$/i.test(signature)) {
    throw new Error('MetaMask returned an invalid permit signature')
  }
  return signature as Hex
}

export type WalletEventHandlers = {
  accountsChanged?: (accounts: Address[]) => void
  chainChanged?: (chainId: number | null) => void
  disconnect?: () => void
}

export const subscribeToMetaMask = (
  provider: EIP1193Provider,
  handlers: WalletEventHandlers,
): (() => void) => {
  const onAccountsChanged = (...args: unknown[]) =>
    handlers.accountsChanged?.(normalizeAccounts(args[0]))
  const onChainChanged = (...args: unknown[]) => {
    const value = args[0]
    try {
      handlers.chainChanged?.(typeof value === 'string' ? Number(BigInt(value)) : null)
    } catch {
      handlers.chainChanged?.(null)
    }
  }
  const onDisconnect = () => handlers.disconnect?.()

  provider.on?.('accountsChanged', onAccountsChanged)
  provider.on?.('chainChanged', onChainChanged)
  provider.on?.('disconnect', onDisconnect)

  return () => {
    provider.removeListener?.('accountsChanged', onAccountsChanged)
    provider.removeListener?.('chainChanged', onChainChanged)
    provider.removeListener?.('disconnect', onDisconnect)
  }
}

export const normalizeWalletError = (error: unknown): string => {
  const code =
    typeof error === 'object' && error && 'code' in error ? Number(error.code) : undefined
  if (code === 4001) return 'The request was rejected in MetaMask.'
  if (code === -32002) return 'A MetaMask request is already waiting for your response.'
  if (error instanceof Error && error.message) return error.message
  return 'MetaMask could not complete the request.'
}
